import React, { useRef } from "react";
import { IconButton, SubtleIconButton } from "components/button";
import { Input } from "components/input";
import DollarInputV2 from "components/input/dollarInputV2";
import { GrDrag } from "react-icons/gr";
import { RiCloseFill } from "react-icons/ri";
import { useDrag, useDrop } from 'react-dnd';
import Switch from "rc-switch";
import { useLineItemContext } from "../context/LineItemContext";

const ProductRow = (props: {
  product: any;
  products: any[];
  formik: any;
  index: number;
  deleteProduct: () => void;
  priceType: "list" | "sale" | "install";
  rateLabel: "list_price" | "unit_price" | "install_price";
  hideEmpty: boolean;
  moveProduct: (dragIndex: number, hoverIndex: number) => void;
  isGroupedProduct: boolean;
}) => {
  const { product, moveProduct, products } = props;
  const { noTax } = useLineItemContext();
  const ITEM_TYPE = "PRODUCT";
  const ref = useRef<HTMLButtonElement>(null);

  const actualIndex = props.index;

  const { isGroupedProduct } = props;

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { index: actualIndex }, 
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ITEM_TYPE,
    hover: (draggedItem: { index: number }) => {
      const dragIndex = draggedItem.index;
      const hoverIndex = actualIndex;
      if (dragIndex === hoverIndex) return;
      
      // Check if drag is allowed based on group constraints
      const draggedProduct = products[dragIndex];
      const hoverProduct = products[hoverIndex];
      
      const draggedInstanceId = draggedProduct?.group_instance_id;
      const hoverInstanceId = hoverProduct?.group_instance_id;
      
      const canDropHere = draggedInstanceId === hoverInstanceId;
      
      if (canDropHere) {
        moveProduct(dragIndex, hoverIndex);
        draggedItem.index = hoverIndex;
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  drag(drop(ref));

  let rate =
    props.priceType === "list"
      ? product.list_price
      : props.priceType === "sale"
      ? product.unit_price
      : product.install_price;
  
  if (!product.quantity && props.hideEmpty) return null;

  return (
    <div className="contents">
      <IconButton size="small" onClick={props.deleteProduct}>
        <RiCloseFill />
      </IconButton>
      <SubtleIconButton 
        size="small" 
        ref={ref}
        className={`
          hover:cursor-grab 
          hover:bg-blue-100
          active:cursor-grabbing 
          ${isDragging ? 'opacity-50' : ''}
          ${isOver && !canDrop ? 'bg-red-100 border-red-300' : ''}
        `}
        title={props.isGroupedProduct ? "Drag to reorder within group" : "Drag to reorder"}
      >
        <GrDrag />
      </SubtleIconButton>
      <Input
        size="small"
        name={`products[${props.index}].quantity`}
        onFocus={(e) => e.target.select()}
        formik={props.formik}
      />
      {product.type === "service" ? (
        <>
          <textarea
            name={`products[${props.index}].label`}
            value={props.formik.values.products[props.index].label}
            onChange={props.formik.handleChange}
            onBlur={props.formik.handleBlur}
            rows={1} // Allow multiple lines
            className="w-full min-h-[30px] border rounded resize-y whitespace-pre-wrap"
          />
          <textarea
            name={`products[${props.index}].long_description`}
            value={props.formik.values.products[props.index].long_description}
            onChange={props.formik.handleChange}
            onBlur={props.formik.handleBlur}
            rows={1} // Allow multiple lines
            className="w-full min-h-[30px] border rounded resize-y whitespace-pre-wrap"
          />
        </>
      ) : (
        <>
          <Input
            size="small"
            name={`products[${props.index}].label`}
            formik={props.formik}
          />
          <Input
            size="small"
            name={`products[${props.index}].long_description`}
            formik={props.formik}
          /> 
        </>
      )}
      <DollarInputV2
        $size="small"
        onFocus={(e) => e.target.select()}
        name={`products[${props.index}].${props.rateLabel}`}
        formik={props.formik}
      />
      <DollarInputV2 disabled $size="small" value={product.quantity * rate} />

      {/*
       * Grid alignment relies on each row producing a consistent number of
       * grid items. When `noTax` is true, do not render any tax-related
       * placeholder cell (even if the product isn't service/part).
       */}
      {isGroupedProduct ? (
        !noTax ? <div /> : null
      ) : !noTax ? (
        <Switch
          checked={props.formik.values.products[props.index].taxable}
          onChange={(checked) => {
            props.formik.setFieldValue(
              `products[${props.index}].taxable`,
              checked
            );
          }}
        />
      ) : null}

    </div>
  )
}

export default ProductRow;